<script setup lang="ts">
import { computed, onBeforeMount, ref, watch, watchEffect } from "vue";
import DocumentDateComparisonDTO = App.Data.Explorer.DocumentDateComparisonDTO;
import DateChip from "@/ui/components/date/DateChip.vue";
import { toRawDeep } from "@/Utils/to-raw-deep";
import { useExplorerStore } from "@/modules/explorer/store";
import DateSelectionWrapper from "@/modules/explorer/components/DateSelectionWrapper.vue";
import Switch from "@/ui/components/switch/switch.vue";
import { usePropertyStore } from "@/modules/explorer/PropertyStore";
import { parse } from "chartjs-graph";
import { maxLength, required } from "@/ui/validation/validationRules";
import { useI18n } from "vue-i18n";
import ColorPicker from "@/ui/components/colorPicker/ColorPicker.vue";
const { t } = useI18n();

const emit = defineEmits(["close"]);
const invalidDateWarning = ref<boolean[]>([]);

const documentModel = useExplorerStore().derivedDocument?.date_comparison;
const comparisonModeOptions = ref([
	{
		value: "current_snapshot",
		label: "Final state (selected snapshot date)",
	},
	{
		value: "same_moment",
		label: "Same date last period",
	},
]);

const customPeriodColors = [
	"#A0C4FF",
	"#FFADAD",
	"#D0A2F7",
	"#B9FBC0",
	"#FFE066",
];

const baseComparisonColor = computed({
	get() {
		return documentToEdit.value.comparisonLabel === "Y"
			? "rgb(38,78,115)"
			: documentToEdit.value.baseComparisonColor;
	},

	set(value) {
		documentToEdit.value.baseComparisonColor = value;
	},
});

let documentToEdit = ref<DocumentDateComparisonDTO>(
	structuredClone(toRawDeep(documentModel))
);

const initialDocument = ref(null);

const modelHasChanged = computed(() => {
	return (
		JSON.stringify(initialDocument.value) !==
		JSON.stringify(documentToEdit.value)
	);
});

const hasHiddenColumns = computed(() => {
	for (const dataTable of useExplorerStore().document.data_tables) {
		if (dataTable.hidden_columns.length > 0) {
			return true;
		}
	}
	return false;
});

const availablePresets = computed(() => {
	const presets: ("B" | number)[] = [-7, -6, -5, -4, -3, -2, -1];

	if (
		usePropertyStore().isAvailableForUser({ name_clean: "budgetdriveout" }) ||
		usePropertyStore().isAvailableForUser({ name_clean: "budget_advertising" })
	) {
		presets.push("B");
	}

	return presets;
});

const presetsEnabled = computed({
	get() {
		const enabled: ("B" | number)[] = [...documentToEdit.value.periods];
		if (documentToEdit.value.isBudgetComparisonActive) {
			enabled.push("B");
		}

		return enabled;
	},
	set(newEnabled) {
		const newWithoutBudget = newEnabled.filter((v) => v !== "B") as number[];
		documentToEdit.value.periods = newWithoutBudget;

		// If they are not the same length then it means budget was filtered out of the array that was set.
		// Which means it's enabled.
		documentToEdit.value.isBudgetComparisonActive =
			newWithoutBudget.length !== newEnabled.length;
	},
});

function setDateValidity(value: boolean, index: number) {
	invalidDateWarning.value[index] = !value;
}

const addEmptyCustomComparison = () => {
	if (documentToEdit.value.customPeriods?.length < 5) {
		documentToEdit.value.customPeriods.push({
			name: "",
			period: {
				from: "now/d",
				to: "now/d",
				from_parsed: parse("now/d").toDate(),
				to_parsed: parse("now/d", true).toDate(),
			},
			color: customPeriodColors[documentToEdit.value.customPeriods.length],
		});
	}
};

watch(
	documentToEdit,
	(value) => {
		let allCustomPeriodsOK = true;
		value?.customPeriods?.forEach((customPeriod) => {
			if (customPeriod.name === "") {
				allCustomPeriodsOK = false;
			}
		});
		if (allCustomPeriodsOK) {
			addEmptyCustomComparison();
		}
	},
	{ deep: true, immediate: true }
);

onBeforeMount(() => {
	documentToEdit.value = structuredClone(
		toRawDeep(useExplorerStore().derivedDocument.date_comparison)
	);
	if (
		documentToEdit.value.customPeriods.find(
			(customPeriod) => customPeriod.period === null
		) === undefined
	) {
		addEmptyCustomComparison();
	}
	// I set initialDocument to the changes after the onBeforeMount here
	initialDocument.value = structuredClone(toRawDeep(documentToEdit.value));
});

const apply = () => {
	// strip empty customPeriods from selection before updating document value.
	const tmpCopyDocumentToEdit = { ...documentToEdit.value };

	tmpCopyDocumentToEdit.customPeriods =
		tmpCopyDocumentToEdit.customPeriods.filter((p) => p.name !== "");

	useExplorerStore().document.date_comparison = tmpCopyDocumentToEdit;

	if (modelHasChanged.value && hasHiddenColumns) {
		for (const table of useExplorerStore().document.data_tables) {
			table.hidden_columns = [];
		}
	}
	closeDialog();
};

const closeDialog = () => {
	emit("close");
};

const removeCustomPeriod = (index: number) => {
	if (index >= 0 && index < documentToEdit.value.customPeriods.length) {
		documentToEdit.value.customPeriods.splice(index, 1);
	}
	invalidDateWarning.value.splice(index, 1);
};

const showPeriodSelection = ref({});
watchEffect(() => {
	for (let i = 0; i < documentToEdit.value.customPeriods.length; i++) {
		if (!(i in showPeriodSelection.value)) {
			showPeriodSelection.value[i] = false;
		}
	}
});

const activeTab = ref("yoy");
const showWarning = computed(() => {
	return (
		documentToEdit.value.customPeriods.length > 1 &&
		documentToEdit.value.periods.length > 0
	);
});

const hasAnyInvalidDates = computed(() =>
	invalidDateWarning.value.includes(true)
);

const baseComparisonSameAsCustomPeriod = computed(() =>
	documentToEdit.value.customPeriods.some(
		(period) =>
			period.name === documentToEdit.value.comparisonLabel && period.name !== ""
	)
);

const baseComparisonSameAsPreset = computed(() => {
	const regex = /^(Y-\d+|B)$/;
	return regex.test(documentToEdit.value.comparisonLabel);
});

const customPeriodNamesHasDuplicates = computed(() => {
	return (
		new Set(documentToEdit.value.customPeriods.map((p) => p.name.toLowerCase()))
			.size !== documentToEdit.value.customPeriods.length
	);
});

const baseComparisonInvalid = computed(() => {
	return (
		baseComparisonSameAsCustomPeriod.value ||
		baseComparisonSameAsPreset.value ||
		customPeriodNamesHasDuplicates.value
	);
});

const isApplyDisabled = computed(
	() =>
		showWarning.value ||
		hasAnyInvalidDates.value ||
		documentToEdit.value.comparisonLabel === "" ||
		baseComparisonInvalid.value
);
</script>

<template>
	<v-card class="comparison-dialog" elevation="0" max-width="600">
		<v-tabs
			v-model="activeTab"
			color="primary"
			slider-color="primary"
			density="comfortable"
		>
			<v-tab value="yoy">
				<v-icon icon="fa-solid fa-calendar-week" class="me-2" />
				Preset Comparisons
			</v-tab>
			<v-tab value="custom">
				<v-icon icon="fa-solid fa-sliders" class="me-2" />
				Custom Periods
			</v-tab>
		</v-tabs>

		<v-divider />

		<v-window v-model="activeTab" class="pa-2">
			<v-window-item value="yoy">
				<div class="py-2 px-2">
					<p class="text-caption mb-2">
						Enable year-over-year comparisons (Y-x) or budget comparison, by
						clicking on the buttons.
					</p>
					<div class="d-flex flex-column">
						<v-btn-toggle
							v-model="presetsEnabled"
							multiple
							divided
							style="height: 40px"
							variant="outlined"
							class="mb-2"
						>
							<v-btn
								v-for="yearOffset in availablePresets"
								:key="yearOffset"
								:value="yearOffset"
								size="small"
							>
								<template v-if="yearOffset === 'B'"> Budget </template>
								<template v-else> Y{{ yearOffset }} </template>
							</v-btn>
						</v-btn-toggle>
						<Switch
							label="Use similar weekday"
							label-position="right"
							:model-value="documentToEdit.mode === 'nearest_similar_weekday'"
							@update:model-value="
								(value) =>
									(documentToEdit.mode = value
										? 'nearest_similar_weekday'
										: 'exact')
							"
						/>
					</div>
				</div>
			</v-window-item>

			<v-window-item value="custom" class="px-2 py-2">
				<div>
					<v-sheet class="rounded-lg overflow-hidden">
						<v-table density="compact">
							<thead>
								<tr>
									<th class="text-left ps-4">Display Name</th>
									<th class="text-left">Date Range</th>
									<th class="text-center">Color</th>
									<th class="text-center" width="80">Actions</th>
								</tr>
							</thead>
							<tbody>
								<tr
									v-for="(period, idx) in documentToEdit.customPeriods"
									:key="idx"
									:class="{
										'empty-row':
											period.name === '' &&
											idx === documentToEdit.customPeriods.length - 1,
									}"
								>
									<td class="pa-2" style="min-width: 180px">
										<v-text-field
											v-model="period.name"
											placeholder="Enter name"
											density="compact"
											hide-details
											class="period-name-field"
											:rules="[required(t), maxLength(t, 100)]"
										/>
									</td>
									<td>
										<v-menu
											:key="`menu_${idx}`"
											:close-on-content-click="false"
											location="bottom"
											v-model="showPeriodSelection[idx]"
											max-width="400"
											transition="slide-y-transition"
										>
											<template v-slot:activator="{ props }">
												<div class="d-flex align-center">
													<v-icon
														icon="fa-solid fa-calendar-days"
														class="mr-2 text-grey"
														size="small"
													/>
													<date-chip
														:key="`chip_${idx}`"
														v-bind="props"
														:input-value="period.period"
														variant="outlined"
														:color="
															invalidDateWarning[idx] ? 'error' : 'default'
														"
														class="date-range-chip"
													/>
												</div>
											</template>

											<v-card elevation="4" rounded="lg">
												<date-selection-wrapper
													:key="`date_selection_${idx}`"
													v-model="period.period"
													:show-presets="false"
													:show-apply="false"
													@date-validation="
														(value) => setDateValidity(value, idx)
													"
													@close="showPeriodSelection[idx] = false"
												/>
											</v-card>
										</v-menu>
									</td>
									<td class="justify-items-center">
										<color-picker
											v-if="period.name"
											v-model="period.color"
											location="center"
										/>
									</td>
									<td class="text-center">
										<v-btn
											v-if="
												idx !== documentToEdit.customPeriods.length - 1 ||
												documentToEdit.customPeriods.length === 5
											"
											@click="removeCustomPeriod(idx)"
											icon
											variant="text"
											color="error"
											size="small"
											class="remove-period-btn"
										>
											<v-icon icon="fa-solid fa-trash" />
										</v-btn>
									</td>
								</tr>
							</tbody>
						</v-table>
					</v-sheet>

					<div class="mt-2 text-caption text-grey">
						<v-icon icon="fa-solid fa-info-circle" size="small" class="me-1" />
						Up to 5 custom periods can be added for comparison.
					</div>
				</div>

				<div class="d-flex flex-row align-center ga-2">
					<v-text-field
						v-model="documentToEdit.comparisonLabel"
						label="Base Comparison Label"
						hint="Name for your current data (appears in legend)"
						persistent-hint
						placeholder="Y"
						variant="outlined"
						density="compact"
						bg-color="surface"
						class="mt-4"
						prepend-inner-icon="fa-solid fa-tag"
					/>
					<color-picker
						v-model="baseComparisonColor"
						v-if="documentToEdit.comparisonLabel !== 'Y'"
					/>
				</div>
			</v-window-item>
		</v-window>

		<v-alert
			v-if="showWarning"
			icon="fa-solid fa-triangle-exclamation"
			color="warning"
			border
			class="mx-2 mb-4"
			variant="tonal"
			density="comfortable"
		>
			<p class="mb-0">
				<strong>Warning:</strong> Please use either preset periods or custom
				periods, but not both at the same time.
			</p>
		</v-alert>
		<v-alert
			v-if="modelHasChanged && hasHiddenColumns"
			variant="tonal"
			color="warning"
			border
			class="mx-2 mb-4"
			density="comfortable"
			>Changing comparisons will reset all of your current hidden
			columns</v-alert
		>
		<v-alert
			v-if="baseComparisonSameAsPreset || baseComparisonSameAsCustomPeriod"
			variant="tonal"
			color="warning"
			border
			class="mx-2 mb-4"
			density="comfortable"
			><strong>Warning:</strong> Base comparison label cant have same name as
			Preset Comparisons or custom display names
		</v-alert>

		<v-alert
			v-if="hasAnyInvalidDates"
			icon="fa-solid fa-triangle-exclamation"
			color="error"
			border
			class="mx-2 mb-4"
			variant="tonal"
			density="comfortable"
		>
			<p class="mb-0">
				<strong>Error:</strong> Invalid date range selected — please correct
				your dates.
			</p>
		</v-alert>

		<div
			class="px-4 pb-4"
			v-if="!useExplorerStore().derivedDocument.vertical.supports_predictions"
		>
			<v-divider class="mb-4" />
			<v-select
				v-model="documentToEdit.compare_with_mode"
				:items="comparisonModeOptions"
				label="Compare with"
				item-title="label"
				item-value="value"
				hide-details
				class="comparison-select flex-grow-1"
			/>
		</div>

		<v-divider />

		<v-card-actions class="pa-2">
			<v-spacer />
			<v-btn
				:disabled="isApplyDisabled"
				prepend-icon="fa-solid fa-check"
				color="primary"
				variant="elevated"
				@click="apply"
			>
				Apply
			</v-btn>
		</v-card-actions>
	</v-card>
</template>

<style scoped lang="scss">
.comparison-dialog {
	border: none;
	overflow: hidden;
	max-width: 800px;
	margin: 0 auto;
}

.comparison-table {
	border: 1px solid rgba(0, 0, 0, 0.08);
	border-radius: 8px;

	:deep(th) {
		font-weight: 600;
		font-size: 0.85rem;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		height: 48px;
	}

	:deep(td) {
		height: 68px;
		vertical-align: middle;
	}

	:deep(tr:hover) {
		background: rgba(0, 0, 0, 0.02);
	}

	:deep(tr.empty-row) {
		background: rgba(0, 0, 0, 0.01);
	}
}

.date-range-chip {
	transition: all 0.2s ease;
	border: 1px solid rgba(0, 0, 0, 0.12);

	&:hover {
		border-color: var(--v-primary-base);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	}
}

.remove-period-btn {
	opacity: 0.7;
	transition: all 0.2s ease;

	&:hover {
		opacity: 1;
		transform: scale(1.1);
	}
}
</style>
